
import React, { useState, useEffect } from "react";
import { Loader2, AlertCircle, ChevronRight, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { hasAdminAccess } from "../../utils/roleAccess";
import api from "../../api"; // Use centralized API client

export default function SessionMain() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);

  const [sessionData, setSessionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const handleFetchSessions = async (page = 1) => {
    setLoading(true);
    setErrorMsg("");
    setSessionData([]);
    try {
      const res = await api.get(
        `api/chatbot/sessions/?page=${page}&page_size=10`,
        { timeout: 30000 }
      );
      const results = res.data?.results || [];
      setSessionData(results);

      if (res.data?.count != null) {
        setTotalPages(Math.max(1, Math.ceil(res.data.count / 10)));
      } else {
        setTotalPages(results.length ? page : 1);
      }
      setCurrentPage(page);
    } catch (e) {
      console.error("Error fetching sessions:", e);
      const detail =
        e.response?.data?.detail ||
        e.response?.data?.message ||
        "Failed to fetch session data.";
      setErrorMsg(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchSessions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    handleFetchSessions(page);
  };

  return (
    <section className="space-y-6 p-6 font-inter bg-slate-50 min-h-screen">
      {/* Actions row */}
      <div className="flex items-center justify-between gap-2">

        {/* Reload */}
        <button
          onClick={() => handleFetchSessions(1)}
          disabled={loading}
          className={`px-5 py-2.5 rounded-xl text-white flex items-center gap-2 font-medium tracking-wide transition-all ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#55c5d0] hover:bg-[#48b7c3]"
            }`}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Fetching Sessions...
            </>
          ) : (
            <>Reload Sessions</>
          )}
        </button>
        {/* Admin-only: Session Analytics */}
        {isAdmin && (
          <button
            onClick={() => navigate("/session-analytics")} // <-- change path if needed
            className="px-5 py-2.5 rounded-xl text-white flex items-center gap-2 font-medium tracking-wide transition-all bg-[#55c5d0] hover:bg-[#48b7c3]"
          >
            <BarChart3 className="w-4 h-4" />
            Session Analytics
          </button>
        )}
      </div>

      {/* Error */}
      {errorMsg && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Table */}
      {!loading && sessionData && sessionData.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#55c5d0] text-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Flow</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Budget</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {sessionData.map((session) => {
                const ctx = session.context || {};
                return (
                  <tr
                    key={session.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    // use RELATIVE navigation since this list is mounted at /session
                    onClick={() => navigate(`${session.id}`)}
                  >
                    <td className="px-4 py-4 text-sm text-slate-700">{session.id ?? "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-slate-700 capitalize">{session.flow || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-slate-700">{session.status || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-slate-700">{ctx.name || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-slate-700">{ctx.email || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-slate-700">{ctx.contact || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-slate-700">{ctx.budget || "N/A"}</td>
                    <td className="px-4 py-4 text-right">
                      <span className="inline-flex items-center gap-1 text-[#55c5d0]">
                        View <ChevronRight className="w-4 h-4" />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 gap-2 flex-wrap">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
          >
            Prev
          </button>

          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => handlePageChange(index + 1)}
              className={`px-3 py-1.5 rounded-lg font-medium ${currentPage === index + 1
                ? "bg-[#55c5d0] text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              {index + 1}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}
